import {
  createContext,
  FunctionComponent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { OptionSelectHandle } from "../OptionSelect";
import { gql, useQuery } from "@shared/utils";
import {QueryResult, useMutation} from '@apollo/client/react';
import {
  CATEGORY_SEARCH,
  PRODUCT_CONFIGURATION_VIEW_QUERY,
} from "../productOptionQuerys";
import {
  SubCategoryMap,
  validOptions,
  isCompatibleCategory,
} from "../../utils/options";
import { groupByCategory } from "../../utils/group";
import {
  productConfigurationToCoreConfig,
  QueryShapeOutput,
  SHAPE_QUERY,
} from "@shared/core-utils";
import { CategoryData, ProductOptionsData } from "./ProductOption";
import { useProductConfigurationVariables } from "../../providers/ProductConfigurationVariables";

const SHAPE_QUERY_GQL = gql`
  ${SHAPE_QUERY}
`;

type FeatureInfo = {
  isCompatible: boolean;
  isAdded: boolean;
  categoriesToAdd?: string[];
  categoriesToRemove?: { id: string; name: string; categoryId?: string }[];
};
interface ProductOptionContextType {
  // Registry for OptionSelect refs to enable debounce cancellation on Cancel
  registerOptionSelect: (subCategoryId: string, handle: OptionSelectHandle | null) => void;
  cancelAllOptionDebounces: (originalItems: Record<string, { options: string[]; variableType: string; number: number; text: string; unit: string }>) => void;
  categoriesUsed: Array<{
    categoryId: string;
    featureId: string;
    optional: boolean;
  }>;
  featuresInfo: Map<string, FeatureInfo>;
  optionsTab: string | null;
  refreshFeatures: () => void;
  setOptionsTab: (tab: string | null) => void;
  selectedCategories: string[];
  setSelectedCategories: (categories: string[]) => void;
  activeLoadingCategory: string | undefined;
  setActiveLoadingCategory: (category: string | undefined) => void;
  productConfigurationQuery: QueryResult<{
    productConfiguration?: ProductOptionsData;
  }>;
  parentProductConfigurationQuery: QueryResult<{
    productConfiguration?: ProductOptionsData;
  }>;
  shapeData: QueryResult<QueryShapeOutput>;
  categoryQuery: QueryResult<{ categorys?: CategoryData[] }>;
  addCategory: (options: { variables: { categoryId: string } }) => Promise<any>;
  loading: boolean;
  catmap: SubCategoryMap;
  parentcatmap: SubCategoryMap;
  categorys: CategoryData[];
  config: any | null;
  productConfigurationId: string;
  showRemove: boolean;
  removeCategoryId: string;
  parentCategoriesOptions: Array<{
    _id: string;
    name: string;
    parentProductCategory?: string;
    useParentValues?: boolean;
  }>;
  parentCategoryIds: string[];
  groupedOptions: Array<{
    items: any[];
    category: any;
  }>;
  parentGroupedOptions: Array<{
    items: any[];
    category: any;
  }>;
  parentCategoriesIds: string[];
  featuresAdded: string[];
  incompatibleFeatures: string[];
  features: QueryResult<{
    productFeaturesByProduct: Array<{
      _id: string;
      name: string;
      categoryIds: string[];
      incompatibleCategoryIds: string[];
      optionalCategoryIds: string[];
      description: string;
      displayOrder: number;
      isActive: boolean;
      createdDate: string;
    }>;
  }>;
  removeCategory: (categoryId: string) => Promise<void>;
}

const REMOVE_PRODUCT_CONFIGURATION_LIST_ITEM = gql`
  mutation RemoveProductConfigurationListItem(
    $subCategoryIds: [String!]
    $productConfigurationId: String!
  ) {
    removeProductConfigurationListItem(
      input: { id: $productConfigurationId, subCategoryIds: $subCategoryIds }
    ) {
      _id
      OptionsList {
        SubCategory {
          name
          maxOptions
          _id
          Category {
            name
            _id
          }
          Options {
            name
            _id
            AllowList {
              subcategory
              options
            }
          }
        }
        options
      }
    }
  }
`;

const ProductOptionContext = createContext<
  ProductOptionContextType | undefined
>(undefined);

export const ProductOptionProvider: FunctionComponent<{
  children: React.ReactNode;
  productConfiguration: string;
  parentProductConfigurationId?: string;
  takeoffId?: string;
}> = ({
  children,
  productConfiguration,
  parentProductConfigurationId,
  takeoffId,
}) => {
  const { tab } = useProductConfigurationVariables();
  const [optionsTab, setOptionsTab] = useState<string | null>("features");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [activeLoadingCategory, setActiveLoadingCategory] = useState<
    string | undefined
  >(undefined);
  const [removeProductConfigurationListItem] = useMutation(
    REMOVE_PRODUCT_CONFIGURATION_LIST_ITEM
  );

  // Registry for OptionSelect refs to enable debounce cancellation on Cancel
  const optionSelectRefs = useRef<Map<string, OptionSelectHandle>>(new Map());

  const registerOptionSelect = useCallback((subCategoryId: string, handle: OptionSelectHandle | null) => {
    if (handle) {
      optionSelectRefs.current.set(subCategoryId, handle);
    } else {
      optionSelectRefs.current.delete(subCategoryId);
    }
  }, []);

  const cancelAllOptionDebounces = useCallback((originalItems: Record<string, { options: string[]; variableType: string; number: number; text: string; unit: string }>) => {
    optionSelectRefs.current.forEach((handle, subCategoryId) => {
      const original = originalItems[subCategoryId];
      if (original) {
        handle.cancelDebounces(original);
      }
    });
  }, []);

  const removeCategory = async (categoryId: string) => {
    const item = groupedOptions.find((g) => g.category?._id == categoryId);
    await removeProductConfigurationListItem({
      variables: {
        subCategoryIds: item?.items.map((i) => i.SubCategory?._id).filter(Boolean) as string[] || [],
        productConfigurationId: productConfiguration,
      },
    });
  };

  const productConfigurationQuery = useQuery<{
    productConfiguration?: ProductOptionsData;
  }>(PRODUCT_CONFIGURATION_VIEW_QUERY, {
    variables: { id: productConfiguration },
    skip: !productConfiguration,
    pollInterval: 1000 * 30,
  });

  const parentProductConfigurationQuery = useQuery<{
    productConfiguration?: ProductOptionsData;
  }>(PRODUCT_CONFIGURATION_VIEW_QUERY, {
    variables: {
      id: parentProductConfigurationId,
    },
    skip: !parentProductConfigurationId,
    pollInterval: 1000 * 30,
  });

  const shapeData = useQuery<QueryShapeOutput>(SHAPE_QUERY_GQL, {
    variables: {
      takeoffId: takeoffId,
    },
    skip: !takeoffId,
  });

  const config = useMemo(() => {
    if (!productConfigurationQuery.data) {
      return undefined;
    }
    const shapes = shapeData.data?.planpageshapes || [];
    return productConfigurationToCoreConfig(
      productConfigurationQuery.data.productConfiguration as any,
      shapes as any
    );
  }, [productConfigurationQuery.data, shapeData.data]);

  const categoryQuery = useQuery<{
    categorys?: CategoryData[];
  }>(CATEGORY_SEARCH, {
    variables: {
      product: productConfigurationQuery?.data?.productConfiguration?.product,
    },
    pollInterval: 1000 * 30,
    skip: !productConfigurationQuery?.data?.productConfiguration?.product,
    fetchPolicy: 'cache-and-network',
  });

  const [addCategory, { loading }] = useMutation(
    gql`
      mutation AddProductConfigurationCategory($categoryId: String!) {
        addProductConfigurationCategory(
          input: { id: "${productConfiguration}", category: $categoryId }
        ) {
          _id
        }
      }
    `
  );

  const categorys = categoryQuery?.data?.categorys || [];

  const catmap = useMemo(() => {
    return validOptions(
      productConfigurationQuery?.data?.productConfiguration?.OptionsList || [],
      categorys,
      config
    );
  }, [
    JSON.stringify(categorys),
    JSON.stringify(productConfigurationQuery?.data?.productConfiguration),
    JSON.stringify(config),
  ]);

  const parentcatmap = useMemo(() => {
    return validOptions(
      parentProductConfigurationQuery?.data?.productConfiguration
        ?.OptionsList || [],
      categorys
    );
  }, [
    JSON.stringify(categorys),
    JSON.stringify(parentProductConfigurationQuery?.data?.productConfiguration),
  ]);

  useEffect(() => {
    if (productConfigurationQuery.data?.productConfiguration?.Categories) {
      setSelectedCategories(
        productConfigurationQuery.data.productConfiguration.Categories.map(
          (c) => c._id
        )
      );
    }
  }, [productConfigurationQuery.data?.productConfiguration?.Categories]);

  const showRemove = useMemo(() => {
    if (!productConfigurationQuery.data?.productConfiguration) return false;
    return /^remove_category/.test(tab || "");
  }, [tab, JSON.stringify(productConfigurationQuery.data)]);

  const removeCategoryId = useMemo(() => {
    return tab?.split("=")[1] || "";
  }, [tab]);

  const parentCategoriesOptions = useMemo(() => {
    if (!productConfigurationQuery.data?.productConfiguration) return [];
    return productConfigurationQuery.data.productConfiguration.Categories.filter(
      (c) => c.useParentValues
    );
  }, [JSON.stringify(productConfigurationQuery.data)]);

  const parentCategoryIds = useMemo(() => {
    return parentCategoriesOptions.map((c) => c.parentProductCategory).filter((id): id is string => !!id);
  }, [JSON.stringify(parentCategoriesOptions)]);

  const groupedOptions = useMemo(() => {
    if (!productConfigurationQuery.data?.productConfiguration) return [];
    return groupByCategory(
      productConfigurationQuery.data.productConfiguration.OptionsList
    ).filter((g) => {
      return !parentCategoryIds.includes(g.category?._id);
    });
  }, [
    JSON.stringify(productConfigurationQuery.data),
    JSON.stringify(parentCategoryIds),
  ]);

  const parentCategoriesIds = useMemo(() => {
    return (
      parentProductConfigurationQuery?.data?.productConfiguration?.Categories?.map(
        (c) => c?._id
      ) || []
    );
  }, [parentProductConfigurationQuery.data]);
  const parentGroupedOptions = useMemo(() => {
    if (!parentProductConfigurationQuery.data?.productConfiguration) return [];
    return groupByCategory(
      parentProductConfigurationQuery.data.productConfiguration.OptionsList ||
        []
    ).filter((g) => {
      return parentCategoryIds.includes(g.category._id);
    });
  }, [
    JSON.stringify(parentProductConfigurationQuery.data),
    JSON.stringify(parentCategoryIds),
  ]);

  const features = useQuery<{
    productFeaturesByProduct: Array<{
      _id: string;
      name: string;
      categoryIds: string[];
      optionalCategoryIds: string[];
      incompatibleCategoryIds: string[];
      description: string;
      displayOrder: number;
      isActive: boolean;
      createdDate: string;
    }>;
  }>(
    gql`
      query productFeaturesByProduct($productId: String!) {
        productFeaturesByProduct(productId: $productId) {
          _id
          name
          categoryIds
          optionalCategoryIds
          incompatibleCategoryIds
          description
          displayOrder
          isActive
          createdDate
        }
      }
    `,
    {
      variables: {
        productId:
          productConfigurationQuery?.data?.productConfiguration?.product,
      },
      skip: !productConfigurationQuery?.data?.productConfiguration?.product,
    }
  );

  const featuresAdded = useMemo(() => {
    const featuresList = features.data?.productFeaturesByProduct || [];
    return featuresList
      .filter((feature) => {
        const requiredCategorys = feature.categoryIds.map((id) => ({
          found: selectedCategories.includes(id),
        }));
        const incompatibleCategorys = feature.incompatibleCategoryIds.map(
          (id) => ({
            found: selectedCategories.includes(id),
          })
        );
        return (
          requiredCategorys.every((c) => c.found) &&
          incompatibleCategorys.every((c) => !c.found)
        );
      })
      .map((feature) => feature._id);
  }, [features.data, selectedCategories]);

  const incompatibleCategories = useMemo(() => {
    const result: Array<string> = [];
    for (const category of categorys) {
      if (!isCompatibleCategory(catmap, category._id, categorys)) {
        result.push(category._id);
      }
    }
    return result;
  }, [JSON.stringify(categorys), JSON.stringify(catmap)]);

  const incompatibleFeatures = useMemo(() => {
    const featuresList = features.data?.productFeaturesByProduct || [];
    return featuresList
      .filter((feature) => {
        const requiredCategorys = feature.categoryIds.map((id) => ({
          compatible: isCompatibleCategory(catmap, id, categorys),
        }));
        const incompatibleCategorys = feature.incompatibleCategoryIds.map(
          (id) => ({
            found: selectedCategories.includes(id),
          })
        );
        return (
          !requiredCategorys.every((c) => c.compatible) ||
          incompatibleCategorys.some((c) => c.found)
        );
      })
      .map((feature) => feature._id);
  }, [
    JSON.stringify(features.data),
    JSON.stringify(catmap),
    JSON.stringify(categorys),
    JSON.stringify(selectedCategories),
  ]);

  const categoriesUsed = useMemo(() => {
    const getAllSubCategories = (categoryId: string) => {
      const category = categorys.find((c) => c._id === categoryId);
      if (!category) return [];

      // Get direct subcategories
      const directSubCategories = category.SubCategorys.map((sub) => ({
        id: sub._id,
        name: sub.name,
      }));

      // Get subcategories from required categories
      const requiredSubCategories = (category.requiredCategorys || []).flatMap(
        (reqCatId) => {
          const reqCategory = categorys.find((c) => c._id === reqCatId);
          return reqCategory
            ? reqCategory.SubCategorys.map((sub) => ({
                id: sub._id,
                name: sub.name,
              }))
            : [];
        }
      );

      return [...directSubCategories, ...requiredSubCategories];
    };
    const result: Array<{
      categoryId: string;
      featureId: string;
      optional: boolean;
    }> = [];
    for (const feature of features.data?.productFeaturesByProduct || []) {
      const expandedCategoryId = feature.categoryIds
        .map((id) => getAllSubCategories(id))
        .flat();
      for (const category of expandedCategoryId) {
        result.push({
          categoryId: category.id,
          featureId: feature._id,
          optional: false,
        });
      }
      const expandedOptionalCategoryId = feature.optionalCategoryIds
        .map((id) => getAllSubCategories(id))
        .flat();
      for (const category of expandedOptionalCategoryId) {
        result.push({
          categoryId: category.id,
          featureId: feature._id,
          optional: true,
        });
      }
    }
    return result;
  }, [features.data]);

  const featuresInfo = useMemo(() => {
    const info = new Map<string, FeatureInfo>();
    features.data?.productFeaturesByProduct?.forEach((feature) => {
      const getAllSubCategories = (categoryId: string) => {
        const category = categorys.find((c) => c._id === categoryId);
        if (!category) return [];

        // Get direct subcategories
        const directSubCategories = category.SubCategorys.map((sub) => ({
          id: sub._id,
          name: sub.name,
          categoryId: category._id,
        }));

        // Get subcategories from required categories
        const requiredSubCategories = (
          category.requiredCategorys || []
        ).flatMap((reqCatId) => {
          const reqCategory = categorys.find((c) => c._id === reqCatId);
          return reqCategory
            ? reqCategory.SubCategorys.map((sub) => ({
                id: sub._id,
                name: sub.name,
                categoryId: reqCategory._id,
              }))
            : [];
        });

        return [...directSubCategories, ...requiredSubCategories];
      };
      const expandedCategoryIds = feature.categoryIds
        .map((id) =>
          getAllSubCategories(id).map((c) => ({
            id: c.id,
            name: c.name,
            categoryId: c.categoryId,
          }))
        )
        .flat();
      const expandedOptionalCategoryIds = feature.optionalCategoryIds
        .map((id) =>
          getAllSubCategories(id).map((c) => ({
            id: c.id,
            name: c.name,
            categoryId: c.categoryId,
          }))
        )
        .flat();
      const categoriesToRemove = [
        ...(expandedCategoryIds || []),
        ...(expandedOptionalCategoryIds || []),
      ].filter(
        ({ id }) =>
          !categoriesUsed.find(
            (c) => c.categoryId === id && c.featureId !== feature._id
          )
      );
      const categoriesToAdd = [
        ...feature.categoryIds,
        ...feature.optionalCategoryIds.filter(
          (id) => !incompatibleCategories.includes(id)
        ),
      ];

      info.set(feature._id, {
        isCompatible: !incompatibleFeatures.includes(feature._id),
        isAdded: featuresAdded.includes(feature._id),
        categoriesToAdd: categoriesToAdd,
        categoriesToRemove,
      });
    });
    return info;
  }, [
    JSON.stringify(features.data),
    JSON.stringify(incompatibleFeatures),
    JSON.stringify(featuresAdded),
    JSON.stringify(categoriesUsed),
    JSON.stringify(categorys),
  ]);

  const value: ProductOptionContextType = {
    registerOptionSelect,
    cancelAllOptionDebounces,
    featuresInfo,
    optionsTab,
    setOptionsTab,
    selectedCategories,
    categoriesUsed,
    setSelectedCategories,
    activeLoadingCategory,
    setActiveLoadingCategory,
    productConfigurationQuery,
    parentProductConfigurationQuery,
    shapeData,
    categoryQuery,
    addCategory,
    loading,
    catmap,
    parentcatmap,
    categorys,
    config,
    productConfigurationId: productConfiguration,
    showRemove,
    removeCategoryId,
    parentCategoriesOptions,
    parentCategoryIds,
    groupedOptions,
    parentGroupedOptions,
    parentCategoriesIds,
    featuresAdded,
    incompatibleFeatures,
    features,
    removeCategory,
    refreshFeatures: () => {
      features.refetch();
    },
  };

  return (
    <ProductOptionContext.Provider value={value}>
      {children}
    </ProductOptionContext.Provider>
  );
};

export const useProductOptionContext = () => {
  const context = useContext(ProductOptionContext);
  if (context === undefined) {
    throw new Error(
      "useProductOptionContext must be used within a ProductOptionProvider"
    );
  }
  return context;
};
